<template>
  <div class="v-row d-flex flex-column gap-4">
    <div
      v-for="localField of localFields"
      :key="`${localField.triggerId}-${localField.formFieldId}`"
      class="d-flex flex-row gap-2">
      <h3 v-if="localField.customField.label">
        {{ localField.customField.label }}:
        <span v-if="localField.customField.required" class="text-error">*</span>
        <help-icon-tooltip v-if="localField.customField.description">
          {{ localField.customField.description }}
        </help-icon-tooltip>
      </h3>
      <component
        v-bind="$attrs"
        v-bind:is="customFieldComponents[localField.customField.type]"
        :field="localField.customField"
        v-model="localField.value"
        single-line
        :timezone="warehouse.timezone"
        :military-time-enabled="$isMilitaryTimeEnabled(warehouse)"
        :warehouse="warehouse" />
    </div>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref, watch } from 'vue';
import { isArray, isNil } from 'lodash';
import { castField, CustomFieldType, CustomFormDataEntitiesEnum } from '@nova/core';
import { useCustomForms } from '@/composables';

import CustomFieldAction from '@/modules/custom_fields/components/field_types/CustomFieldAction.vue';
import CustomFieldBigstring from '@/modules/custom_fields/components/field_types/CustomFieldBigstring.vue';
import CustomFieldBool from '@/modules/custom_fields/components/field_types/CustomFieldBool.vue';
import CustomFieldCombobox from '@/modules/custom_fields/components/field_types/CustomFieldCombobox.vue';
import CustomFieldDate from '@/modules/custom_fields/components/field_types/CustomFieldDate.vue';
import CustomFieldDocument from '@/modules/custom_fields/components/field_types/CustomFieldDocument.vue';
import CustomFieldDropdownmultiselect from '@/modules/custom_fields/components/field_types/CustomFieldDropdownmultiselect.vue';
import CustomFieldDropdown from '@/modules/custom_fields/components/field_types/CustomFieldDropdown.vue';
import CustomFieldEmail from '@/modules/custom_fields/components/field_types/CustomFieldEmail.vue';
import CustomFieldMultidocument from '@/modules/custom_fields/components/field_types/CustomFieldMultidocument.vue';
import CustomFieldNumber from '@/modules/custom_fields/components/field_types/CustomFieldNumber.vue';
import CustomFieldPhone from '@/modules/custom_fields/components/field_types/CustomFieldPhone.vue';
import CustomFieldString from '@/modules/custom_fields/components/field_types/CustomFieldString.vue';
import CustomFieldTimestamp from '@/modules/custom_fields/components/field_types/CustomFieldTimestamp.vue';

export default defineComponent({
  name: 'CreateAppointmentCustomForms',
  emits: ['update:value'],
  props: {
    triggers: {
      type: Array,
      required: true
    },
    warehouse: {
      type: Object,
      required: true
    },
    initialCustomFormsData: {
      type: Array,
      default: () => []
    }
  },
  components: {
    CustomFieldAction,
    CustomFieldBigstring,
    CustomFieldBool,
    CustomFieldCombobox,
    CustomFieldDate,
    CustomFieldDocument,
    CustomFieldDropdownmultiselect,
    CustomFieldDropdown,
    CustomFieldEmail,
    CustomFieldMultidocument,
    CustomFieldNumber,
    CustomFieldPhone,
    CustomFieldString,
    CustomFieldTimestamp
  },
  setup(props, { emit }) {
    const { getCustomField, customFieldComponents } = useCustomForms();
    const localFields = ref([]);
    const propagateValueChange = () =>
      emit(
        'update:value',
        localFields.value.map(field => ({
          formFieldId: field.formFieldId,
          triggerId: field.triggerId,
          value: field.value
        }))
      );

    const parseClonedFieldValue = (rawValue, fieldType) => {
      if (isNil(rawValue)) {
        return fieldType === CustomFieldType.Bool ? false : null;
      }
      // Values may already be arrays (e.g. multidoc/dropdownmultiselect after
      // upstream normalization); pass them through without re-parsing.
      if (isArray(rawValue)) return rawValue;
      // castField expects a string, but the API may return numeric values for
      // Number (int) fields because the @AfterLoad hook already casts them.
      // Convert to string so castField can process every type uniformly.
      const casted = castField(fieldType, String(rawValue));
      // castField returns null for bool values it can't parse; default to false.
      return fieldType === CustomFieldType.Bool ? casted ?? false : casted;
    };

    const setLocalFields = () => {
      for (const trigger of props.triggers.filter(trigger => Boolean(trigger.flow?.formFrom))) {
        for (const formField of trigger.flow.formFrom.formFields.sort(
          (a, b) => a.order - b.order
        )) {
          const fieldType = formField.field.type;
          const existingData = props.initialCustomFormsData.find(
            d => d.formFieldId === formField.id && d.triggerId === trigger.id
          );
          const defaultValue = fieldType === CustomFieldType.Bool ? false : null;
          const initialValue =
            existingData != null
              ? parseClonedFieldValue(existingData.value, fieldType)
              : defaultValue;

          const customField = getCustomField(formField);
          customField.value = initialValue;

          localFields.value.push({
            form: trigger.flow.formFrom,
            customField,
            entityName: CustomFormDataEntitiesEnum.appointment,
            formFieldId: formField.id,
            triggerId: trigger.id,
            value: initialValue
          });
        }
      }
    };

    // Build the initial value for the request
    onMounted(() => {
      setLocalFields();
      propagateValueChange();
    });

    watch(localFields, () => propagateValueChange(), { deep: true });

    return {
      propagateValueChange,
      localFields,
      customFieldComponents
    };
  }
});
</script>
